/**
 * TallyXmlHelper.js
 * Helper for generating Tally XML import files for Sales Vouchers.
 */

function escapeXml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function formatDateToTally(dateStr) {
  if (!dateStr) {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) {
    const digits = String(dateStr).replace(/[^0-9]/g, '');
    if (digits.length >= 8) {
      return digits.slice(0, 8);
    }
    const today = new Date();
    return `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

function generateGuid(saleId, index) {
  const timestamp = Date.now().toString(16);
  const padIndex = String(index + 1).padStart(8, '0');
  return `pos-${saleId || timestamp}-${padIndex}`;
}

/**
 * Generates Tally XML import document for Sales Vouchers.
 * @param {Array} sales - Array of sale/bill objects
 * @param {Object} options - Company & shop info (companyName, state, gstin, enteredBy)
 * @returns {String} Tally XML formatted string
 */
export function generateTallySalesXml(sales = [], options = {}) {
  const companyName = options.companyName || options.shopName || options.name || 'MACHAD MILL';
  const companyState = options.state || options.shopState || 'Kerala';
  const companyGstin = options.gstin || options.tax_number || options.config?.gstin || '32AAMFL4079J1Z0';
  const enteredBy = options.userName || options.shopName || companyName;

  let xml = `<ENVELOPE>\n`;
  xml += ` <HEADER>\n`;
  xml += `  <TALLYREQUEST>Import Data</TALLYREQUEST>\n`;
  xml += ` </HEADER>\n`;
  xml += ` <BODY>\n`;
  xml += `  <IMPORTDATA>\n`;
  xml += `   <REQUESTDESC>\n`;
  xml += `    <REPORTNAME>Vouchers</REPORTNAME>\n`;
  xml += `    <STATICVARIABLES>\n`;
  xml += `     <SVCURRENTCOMPANY>${escapeXml(companyName)}</SVCURRENTCOMPANY>\n`;
  xml += `    </STATICVARIABLES>\n`;
  xml += `   </REQUESTDESC>\n`;
  xml += `   <REQUESTDATA>\n`;

  sales.forEach((sale, index) => {
    xml += buildVoucherXml(sale, index, { companyName, companyState, companyGstin, enteredBy });
  });

  xml += `   </REQUESTDATA>\n`;
  xml += `  </IMPORTDATA>\n`;
  xml += ` </BODY>\n`;
  xml += `</ENVELOPE>`;

  return xml;
}

function buildVoucherXml(sale, index, config) {
  const billNo = sale.bill_no || sale.fid || `BILL-${index + 1}`;
  const dateFormatted = formatDateToTally(sale.date || sale.created_at || sale.time);
  const guid = sale.guid || sale.uuid || generateGuid(sale.id, index);
  const remoteId = `${guid}-00000e04`;
  const vchKey = `${guid}-0000b4ba:00000010`;

  const partyName = sale.customer_name || sale.customer?.name || sale.party_name || 'Local Sales';
  const partyGstin = sale.customer_tax_id || sale.customer?.tax_number || sale.customer_gstin || '';
  const gstRegistrationType = partyGstin ? 'Regular' : 'Unregistered/Consumer';
  const stateName = sale.customer?.state || sale.state || config.companyState;
  const narration = sale.narration || sale.remarks || 'STORE SALE';

  const totalAmount = parseFloat(sale.total || sale.net_amount || sale.payable || 0);
  const totalTaxAmount = parseFloat(sale.tax_amount || sale.tax || 0);
  const cessAmount = parseFloat(sale.cess_amount || sale.cess || 0);
  const discountAmount = parseFloat(sale.discount || 0) + parseFloat(sale.discount_applied_on_items || 0);

  const isInterstate = sale.is_interstate || false;
  const cgstAmount = isInterstate ? 0 : totalTaxAmount / 2;
  const sgstAmount = isInterstate ? 0 : totalTaxAmount / 2;
  const igstAmount = isInterstate ? totalTaxAmount : 0;

  const items = sale.items || [];
  const entryMode = items.length > 0 ? 'Item Invoice' : 'Item Invoice';

  let vXml = `    <TALLYMESSAGE xmlns:UDF="TallyUDF">\n`;
  vXml += `     <VOUCHER REMOTEID="${escapeXml(remoteId)}" VCHKEY="${escapeXml(vchKey)}" VCHTYPE="Sales" ACTION="Create" OBJVIEW="Invoice Voucher View">\n`;
  vXml += `      <OLDAUDITENTRYIDS.LIST TYPE="Number">\n`;
  vXml += `       <OLDAUDITENTRYIDS>-1</OLDAUDITENTRYIDS>\n`;
  vXml += `      </OLDAUDITENTRYIDS.LIST>\n`;
  vXml += `      <DATE>${dateFormatted}</DATE>\n`;
  vXml += `      <VCHSTATUSDATE>${dateFormatted}</VCHSTATUSDATE>\n`;
  vXml += `      <GUID>${escapeXml(guid)}</GUID>\n`;
  vXml += `      <GSTREGISTRATIONTYPE>${gstRegistrationType}</GSTREGISTRATIONTYPE>\n`;
  vXml += `      <VATDEALERTYPE>&#4; Unknown</VATDEALERTYPE>\n`;
  vXml += `      <STATENAME>${escapeXml(stateName)}</STATENAME>\n`;
  vXml += `      <NARRATION>${escapeXml(narration)}</NARRATION>\n`;
  vXml += `      <ENTEREDBY>${escapeXml(config.enteredBy)}</ENTEREDBY>\n`;
  vXml += `      <COUNTRYOFRESIDENCE>India</COUNTRYOFRESIDENCE>\n`;
  vXml += `      <PLACEOFSUPPLY>${escapeXml(stateName)}</PLACEOFSUPPLY>\n`;
  vXml += `      <VOUCHERTYPENAME>Sales</VOUCHERTYPENAME>\n`;
  vXml += `      <PARTYNAME>${escapeXml(partyName)}</PARTYNAME>\n`;
  
  if (config.companyGstin) {
    vXml += `      <GSTREGISTRATION TAXTYPE="GST" TAXREGISTRATION="${escapeXml(config.companyGstin)}">${escapeXml(config.companyState)} Registration</GSTREGISTRATION>\n`;
    vXml += `      <CMPGSTIN>${escapeXml(config.companyGstin)}</CMPGSTIN>\n`;
  }
  
  vXml += `      <PARTYLEDGERNAME>${escapeXml(partyName)}</PARTYLEDGERNAME>\n`;
  vXml += `      <VOUCHERNUMBER>${escapeXml(billNo)}</VOUCHERNUMBER>\n`;
  vXml += `      <BASICBUYERNAME>${escapeXml(partyName)}</BASICBUYERNAME>\n`;
  vXml += `      <CMPGSTREGISTRATIONTYPE>Regular</CMPGSTREGISTRATIONTYPE>\n`;
  vXml += `      <PARTYMAILINGNAME>${escapeXml(partyName)}</PARTYMAILINGNAME>\n`;
  vXml += `      <CONSIGNEEMAILINGNAME>${escapeXml(partyName)}</CONSIGNEEMAILINGNAME>\n`;
  vXml += `      <CONSIGNEESTATENAME>${escapeXml(stateName)}</CONSIGNEESTATENAME>\n`;
  vXml += `      <CMPGSTSTATE>${escapeXml(config.companyState)}</CMPGSTSTATE>\n`;
  vXml += `      <CONSIGNEECOUNTRYNAME>India</CONSIGNEECOUNTRYNAME>\n`;
  vXml += `      <BASICBASEPARTYNAME>${escapeXml(partyName)}</BASICBASEPARTYNAME>\n`;
  vXml += `      <NUMBERINGSTYLE>Auto Renumber</NUMBERINGSTYLE>\n`;
  vXml += `      <CSTFORMISSUETYPE>&#4; Not Applicable</CSTFORMISSUETYPE>\n`;
  vXml += `      <CSTFORMRECVTYPE>&#4; Not Applicable</CSTFORMRECVTYPE>\n`;
  vXml += `      <FBTPAYMENTTYPE>Default</FBTPAYMENTTYPE>\n`;
  vXml += `      <PERSISTEDVIEW>Invoice Voucher View</PERSISTEDVIEW>\n`;
  vXml += `      <VCHSTATUSTAXADJUSTMENT>Default</VCHSTATUSTAXADJUSTMENT>\n`;
  vXml += `      <VCHSTATUSVOUCHERTYPE>Sales</VCHSTATUSVOUCHERTYPE>\n`;
  vXml += `      <VCHSTATUSTAXUNIT>${escapeXml(config.companyState)} Registration</VCHSTATUSTAXUNIT>\n`;
  vXml += `      <VCHGSTCLASS>&#4; Not Applicable</VCHGSTCLASS>\n`;
  vXml += `      <VCHENTRYMODE>${entryMode}</VCHENTRYMODE>\n`;
  vXml += `      <DIFFACTUALQTY>No</DIFFACTUALQTY>\n`;
  vXml += `      <ISMSTFROMSYNC>No</ISMSTFROMSYNC>\n`;
  vXml += `      <ISDELETED>No</ISDELETED>\n`;
  vXml += `      <ISSECURITYONWHENENTERED>No</ISSECURITYONWHENENTERED>\n`;
  vXml += `      <ASORIGINAL>No</ASORIGINAL>\n`;
  vXml += `      <AUDITED>No</AUDITED>\n`;
  vXml += `      <ISCOMMONPARTY>No</ISCOMMONPARTY>\n`;
  vXml += `      <FORJOBCOSTING>No</FORJOBCOSTING>\n`;
  vXml += `      <ISOPTIONAL>No</ISOPTIONAL>\n`;
  vXml += `      <EFFECTIVEDATE>${dateFormatted}</EFFECTIVEDATE>\n`;
  vXml += `      <USEFOREXCISE>No</USEFOREXCISE>\n`;
  vXml += `      <ISFORJOBWORKIN>No</ISFORJOBWORKIN>\n`;
  vXml += `      <ALLOWCONSUMPTION>No</ALLOWCONSUMPTION>\n`;
  vXml += `      <USEFORINTEREST>No</USEFORINTEREST>\n`;
  vXml += `      <USEFORGAINLOSS>No</USEFORGAINLOSS>\n`;
  vXml += `      <USEFORGODOWNTRANSFER>No</USEFORGODOWNTRANSFER>\n`;
  vXml += `      <USEFORCOMPOUND>No</USEFORCOMPOUND>\n`;
  vXml += `      <USEFORSERVICETAX>No</USEFORSERVICETAX>\n`;
  vXml += `      <ISREVERSECHARGEAPPLICABLE>No</ISREVERSECHARGEAPPLICABLE>\n`;
  vXml += `      <ISSYSTEM>No</ISSYSTEM>\n`;
  vXml += `      <ISFETCHEDONLY>No</ISFETCHEDONLY>\n`;
  vXml += `      <ISGSTOVERRIDDEN>No</ISGSTOVERRIDDEN>\n`;
  vXml += `      <ISCANCELLED>No</ISCANCELLED>\n`;
  vXml += `      <ISONHOLD>No</ISONHOLD>\n`;
  vXml += `      <ISSUMMARY>No</ISSUMMARY>\n`;
  vXml += `      <ISECOMMERCESUPPLY>No</ISECOMMERCESUPPLY>\n`;
  vXml += `      <ISBOENOTAPPLICABLE>No</ISBOENOTAPPLICABLE>\n`;
  vXml += `      <ISGSTSECSEVENAPPLICABLE>No</ISGSTSECSEVENAPPLICABLE>\n`;
  vXml += `      <IGNOREEINVVALIDATION>No</IGNOREEINVVALIDATION>\n`;
  vXml += `      <CMPGSTISOTHTERRITORYASSESSEE>No</CMPGSTISOTHTERRITORYASSESSEE>\n`;
  vXml += `      <PARTYGSTISOTHTERRITORYASSESSEE>No</PARTYGSTISOTHTERRITORYASSESSEE>\n`;
  vXml += `      <IRNJSONEXPORTED>No</IRNJSONEXPORTED>\n`;
  vXml += `      <IRNCANCELLED>No</IRNCANCELLED>\n`;
  vXml += `      <IGNOREGSTCONFLICTINMIG>No</IGNOREGSTCONFLICTINMIG>\n`;
  vXml += `      <ISOPBALTRANSACTION>No</ISOPBALTRANSACTION>\n`;
  vXml += `      <IGNOREGSTFORMATVALIDATION>No</IGNOREGSTFORMATVALIDATION>\n`;
  vXml += `      <ISELIGIBLEFORITC>Yes</ISELIGIBLEFORITC>\n`;
  vXml += `      <IGNOREGSTOPTIONALUNCERTAIN>No</IGNOREGSTOPTIONALUNCERTAIN>\n`;
  vXml += `      <ISGSTREFUND>No</ISGSTREFUND>\n`;
  vXml += `      <ISEWAYBILLEXCLUDED>No</ISEWAYBILLEXCLUDED>\n`;
  vXml += `      <ISBUYERPEPPOLREGISTERED>No</ISBUYERPEPPOLREGISTERED>\n`;
  vXml += `      <ISVATOVERRIDDEN>No</ISVATOVERRIDDEN>\n`;
  vXml += `      <ISEINVOVERRIDDEN>No</ISEINVOVERRIDDEN>\n`;
  vXml += `      <UPDATESUMMARYVALUES>No</UPDATESUMMARYVALUES>\n`;
  vXml += `      <ISEWAYBILLAPPLICABLE>No</ISEWAYBILLAPPLICABLE>\n`;
  vXml += `      <ISDELETEDRETAINED>No</ISDELETEDRETAINED>\n`;
  vXml += `      <ISNULL>No</ISNULL>\n`;
  vXml += `      <ISEXCISEVOUCHER>No</ISEXCISEVOUCHER>\n`;
  vXml += `      <EXCISETAXOVERRIDE>No</EXCISETAXOVERRIDE>\n`;
  vXml += `      <USEFORTAXUNITTRANSFER>No</USEFORTAXUNITTRANSFER>\n`;
  vXml += `      <ISEXER1NOPOVERWRITE>No</ISEXER1NOPOVERWRITE>\n`;
  vXml += `      <ISEXF2NOPOVERWRITE>No</ISEXF2NOPOVERWRITE>\n`;
  vXml += `      <ISEXER3NOPOVERWRITE>No</ISEXER3NOPOVERWRITE>\n`;
  vXml += `      <IGNOREPOSVALIDATION>No</IGNOREPOSVALIDATION>\n`;
  vXml += `      <EXCISEOPENING>No</EXCISEOPENING>\n`;
  vXml += `      <USEFORFINALPRODUCTION>No</USEFORFINALPRODUCTION>\n`;
  vXml += `      <ISTDSOVERRIDDEN>No</ISTDSOVERRIDDEN>\n`;
  vXml += `      <ISTCSOVERRIDDEN>No</ISTCSOVERRIDDEN>\n`;
  vXml += `      <ISTDSTCSCASHVCH>No</ISTDSTCSCASHVCH>\n`;
  vXml += `      <INCLUDEADVPYMTVCH>No</INCLUDEADVPYMTVCH>\n`;
  vXml += `      <ISSUBWORKSCONTRACT>No</ISSUBWORKSCONTRACT>\n`;
  vXml += `      <IGNOREORIGVCHDATE>No</IGNOREORIGVCHDATE>\n`;
  vXml += `      <ISVATPAIDATCUSTOMS>No</ISVATPAIDATCUSTOMS>\n`;
  vXml += `      <ISDECLAREDTOCUSTOMS>No</ISDECLAREDTOCUSTOMS>\n`;
  vXml += `      <VATADVANCEPAYMENT>No</VATADVANCEPAYMENT>\n`;
  vXml += `      <VATADVPAY>No</VATADVPAY>\n`;
  vXml += `      <ISCSTDELCAREDGOODSSALES>No</ISCSTDELCAREDGOODSSALES>\n`;
  vXml += `      <ISVATRESTAXINVOICE>No</ISVATRESTAXINVOICE>\n`;
  vXml += `      <ISSERVICETAXOVERRIDDEN>No</ISSERVICETAXOVERRIDDEN>\n`;
  vXml += `      <ISISDVOUCHER>No</ISISDVOUCHER>\n`;
  vXml += `      <ISEXCISEOVERRIDDEN>No</ISEXCISEOVERRIDDEN>\n`;
  vXml += `      <ISEXCISESUPPLYVCH>No</ISEXCISESUPPLYVCH>\n`;
  vXml += `      <GSTNOTEXPORTED>No</GSTNOTEXPORTED>\n`;
  vXml += `      <IGNOREGSTINVALIDATION>No</IGNOREGSTINVALIDATION>\n`;
  vXml += `      <OVRDNEWAYBILLAPPLICABILITY>No</OVRDNEWAYBILLAPPLICABILITY>\n`;
  vXml += `      <ISVATPRINCIPALACCOUNT>No</ISVATPRINCIPALACCOUNT>\n`;
  vXml += `      <VCHSTATUSISVCHNUMUSED>No</VCHSTATUSISVCHNUMUSED>\n`;
  vXml += `      <VCHGSTSTATUSISINCLUDED>Yes</VCHGSTSTATUSISINCLUDED>\n`;
  vXml += `      <VCHGSTSTATUSISUNCERTAIN>No</VCHGSTSTATUSISUNCERTAIN>\n`;
  vXml += `      <VCHGSTSTATUSISEXCLUDED>No</VCHGSTSTATUSISEXCLUDED>\n`;
  vXml += `      <VCHGSTSTATUSISAPPLICABLE>Yes</VCHGSTSTATUSISAPPLICABLE>\n`;
  vXml += `      <VCHGSTSTATUSISGSTR2BRECONCILED>No</VCHGSTSTATUSISGSTR2BRECONCILED>\n`;
  vXml += `      <VCHGSTSTATUSISGSTR2BONLYINPORTAL>No</VCHGSTSTATUSISGSTR2BONLYINPORTAL>\n`;
  vXml += `      <VCHGSTSTATUSISGSTR2BONLYINBOOKS>No</VCHGSTSTATUSISGSTR2BONLYINBOOKS>\n`;
  vXml += `      <VCHGSTSTATUSISGSTR2BMISMATCH>No</VCHGSTSTATUSISGSTR2BMISMATCH>\n`;
  vXml += `      <VCHGSTSTATUSISGSTR2BINDIFFPERIOD>No</VCHGSTSTATUSISGSTR2BINDIFFPERIOD>\n`;
  vXml += `      <VCHGSTSTATUSISRETEFFDATEOVERRDN>No</VCHGSTSTATUSISRETEFFDATEOVERRDN>\n`;
  vXml += `      <VCHGSTSTATUSISOVERRDN>No</VCHGSTSTATUSISOVERRDN>\n`;
  vXml += `      <VCHGSTSTATUSISSTATINDIFFDATE>No</VCHGSTSTATUSISSTATINDIFFDATE>\n`;
  vXml += `      <VCHGSTSTATUSISRETINDIFFDATE>No</VCHGSTSTATUSISRETINDIFFDATE>\n`;
  vXml += `      <VCHGSTSTATUSMAINSECTIONEXCLUDED>No</VCHGSTSTATUSMAINSECTIONEXCLUDED>\n`;
  vXml += `      <VCHGSTSTATUSISBRANCHTRANSFEROUT>No</VCHGSTSTATUSISBRANCHTRANSFEROUT>\n`;
  vXml += `      <VCHGSTSTATUSISSYSTEMSUMMARY>No</VCHGSTSTATUSISSYSTEMSUMMARY>\n`;
  vXml += `      <VCHSTATUSISUNREGISTEREDRCM>No</VCHSTATUSISUNREGISTEREDRCM>\n`;
  vXml += `      <VCHSTATUSISOPTIONAL>No</VCHSTATUSISOPTIONAL>\n`;
  vXml += `      <VCHSTATUSISCANCELLED>No</VCHSTATUSISCANCELLED>\n`;
  vXml += `      <VCHSTATUSISDELETED>No</VCHSTATUSISDELETED>\n`;
  vXml += `      <VCHSTATUSISOPENINGBALANCE>No</VCHSTATUSISOPENINGBALANCE>\n`;
  vXml += `      <VCHSTATUSISFETCHEDONLY>No</VCHSTATUSISFETCHEDONLY>\n`;
  vXml += `      <VCHGSTSTATUSISOPTIONALUNCERTAIN>No</VCHGSTSTATUSISOPTIONALUNCERTAIN>\n`;
  vXml += `      <VCHSTATUSISREACCEPTFORHSNDONE>No</VCHSTATUSISREACCEPTFORHSNDONE>\n`;
  vXml += `      <VCHSTATUSISREACCEPHSNSIXONEDONE>Yes</VCHSTATUSISREACCEPHSNSIXONEDONE>\n`;
  vXml += `      <PAYMENTLINKHASMULTIREF>No</PAYMENTLINKHASMULTIREF>\n`;
  vXml += `      <ISSHIPPINGWITHINSTATE>No</ISSHIPPINGWITHINSTATE>\n`;
  vXml += `      <ISOVERSEASTOURISTTRANS>No</ISOVERSEASTOURISTTRANS>\n`;
  vXml += `      <ISDESIGNATEDZONEPARTY>No</ISDESIGNATEDZONEPARTY>\n`;
  vXml += `      <HASCASHFLOW>No</HASCASHFLOW>\n`;
  vXml += `      <ISPOSTDATED>No</ISPOSTDATED>\n`;
  vXml += `      <USETRACKINGNUMBER>No</USETRACKINGNUMBER>\n`;
  vXml += `      <ISINVOICE>Yes</ISINVOICE>\n`;
  vXml += `      <MFGJOURNAL>No</MFGJOURNAL>\n`;
  vXml += `      <HASDISCOUNTS>No</HASDISCOUNTS>\n`;
  vXml += `      <ASPAYSLIP>No</ASPAYSLIP>\n`;
  vXml += `      <ISCOSTCENTRE>No</ISCOSTCENTRE>\n`;
  vXml += `      <ISSTXNONREALIZEDVCH>No</ISSTXNONREALIZEDVCH>\n`;
  vXml += `      <ISEXCISEMANUFACTURERON>No</ISEXCISEMANUFACTURERON>\n`;
  vXml += `      <ISBLANKCHEQUE>No</ISBLANKCHEQUE>\n`;
  vXml += `      <ISVOID>No</ISVOID>\n`;
  vXml += `      <ORDERLINESTATUS>No</ORDERLINESTATUS>\n`;
  vXml += `      <VATISAGNSTCANCSALES>No</VATISAGNSTCANCSALES>\n`;
  vXml += `      <VATISPURCEXEMPTED>No</VATISPURCEXEMPTED>\n`;
  vXml += `      <ISVATRESTAXINVOICE>No</ISVATRESTAXINVOICE>\n`;
  vXml += `      <VATISASSESABLECALCVCH>No</VATISASSESABLECALCVCH>\n`;
  vXml += `      <ISVATDUTYPAID>Yes</ISVATDUTYPAID>\n`;
  vXml += `      <ISDELIVERYSAMEASCONSIGNEE>No</ISDELIVERYSAMEASCONSIGNEE>\n`;
  vXml += `      <ISDISPATCHSAMEASCONSIGNOR>No</ISDISPATCHSAMEASCONSIGNOR>\n`;
  vXml += `      <ISDELETEDVCHRETAINED>No</ISDELETEDVCHRETAINED>\n`;
  vXml += `      <VCHONLYADDLINFOUPDATED>No</VCHONLYADDLINFOUPDATED>\n`;
  vXml += `      <CHANGEVCHMODE>No</CHANGEVCHMODE>\n`;
  vXml += `      <UAEEINVISBILLPREPAIDAMTOVRDN>No</UAEEINVISBILLPREPAIDAMTOVRDN>\n`;
  vXml += `      <RESETIRNQRCODE>No</RESETIRNQRCODE>\n`;
  vXml += `      <ISVCHEXCHANGED>No</ISVCHEXCHANGED>\n`;
  vXml += `      <VOUCHERNUMBERSERIES>Default</VOUCHERNUMBERSERIES>\n`;
  vXml += `      <EWAYBILLDETAILS.LIST>      </EWAYBILLDETAILS.LIST>\n`;
  vXml += `      <EXCLUDEDTAXATIONS.LIST>      </EXCLUDEDTAXATIONS.LIST>\n`;
  vXml += `      <OLDAUDITENTRIES.LIST>      </OLDAUDITENTRIES.LIST>\n`;
  vXml += `      <ACCOUNTAUDITENTRIES.LIST>      </ACCOUNTAUDITENTRIES.LIST>\n`;
  vXml += `      <AUDITENTRIES.LIST>      </AUDITENTRIES.LIST>\n`;
  vXml += `      <DUTYHEADDETAILS.LIST>      </DUTYHEADDETAILS.LIST>\n`;
  vXml += `      <GSTADVADJDETAILS.LIST>      </GSTADVADJDETAILS.LIST>\n`;

  // Inventory Entries
  if (items.length > 0) {
    items.forEach((itemRow) => {
      const itemName = itemRow.item?.name || itemRow.name || itemRow.description || 'Sales Item';
      const qty = parseFloat(itemRow.quantity || itemRow.qty || 1);
      const unit = itemRow.unit || itemRow.item?.unit || 'KG';
      const rate = parseFloat(itemRow.rate || itemRow.price || itemRow.unit_price || 0);
      const taxableAmt = parseFloat(itemRow.payable || itemRow.amount || (rate * qty) || 0);
      const hsn = itemRow.hsn || itemRow.item?.hsn_code || itemRow.hsn_code || '15131900';
      const hsnDesc = itemRow.hsn_description || 'Other';

      const itemTaxRate = parseFloat(itemRow.tax || itemRow.tax_percentage || itemRow.tax_rate || 5);
      const itemCgstRate = isInterstate ? 0 : (itemTaxRate / 2);
      const itemSgstRate = isInterstate ? 0 : (itemTaxRate / 2);
      const itemIgstRate = isInterstate ? itemTaxRate : 0;
      const godown = itemRow.godown || itemRow.warehouse || config.companyState;

      vXml += `      <ALLINVENTORYENTRIES.LIST>\n`;
      vXml += `       <STOCKITEMNAME>${escapeXml(itemName)}</STOCKITEMNAME>\n`;
      vXml += `       <GSTOVRDNISREVCHARGEAPPL>&#4; Not Applicable</GSTOVRDNISREVCHARGEAPPL>\n`;
      vXml += `       <GSTOVRDNTAXABILITY>Taxable</GSTOVRDNTAXABILITY>\n`;
      vXml += `       <GSTSOURCETYPE>Stock Item</GSTSOURCETYPE>\n`;
      vXml += `       <GSTITEMSOURCE>${escapeXml(itemName)}</GSTITEMSOURCE>\n`;
      vXml += `       <HSNSOURCETYPE>Stock Item</HSNSOURCETYPE>\n`;
      vXml += `       <HSNITEMSOURCE>${escapeXml(itemName)}</HSNITEMSOURCE>\n`;
      vXml += `       <GSTOVRDNSTOREDNATURE/>\n`;
      vXml += `       <GSTOVRDNTYPEOFSUPPLY>Goods</GSTOVRDNTYPEOFSUPPLY>\n`;
      vXml += `       <GSTRATEINFERAPPLICABILITY>As per Masters/Company</GSTRATEINFERAPPLICABILITY>\n`;
      vXml += `       <GSTHSNNAME>${escapeXml(hsn)}</GSTHSNNAME>\n`;
      vXml += `       <GSTHSNDESCRIPTION>${escapeXml(hsnDesc)}</GSTHSNDESCRIPTION>\n`;
      vXml += `       <GSTHSNINFERAPPLICABILITY>As per Masters/Company</GSTHSNINFERAPPLICABILITY>\n`;
      vXml += `       <GSTOVRDNISTAXONMRPAPPLICABLE>&#4; Not Applicable</GSTOVRDNISTAXONMRPAPPLICABLE>\n`;
      vXml += `       <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>\n`;
      vXml += `       <ISRCMAPPLICABLE>No</ISRCMAPPLICABLE>\n`;
      vXml += `       <ISGSTASSESSABLEVALUEOVERRIDDEN>No</ISGSTASSESSABLEVALUEOVERRIDDEN>\n`;
      vXml += `       <STRDISGSTAPPLICABLE>No</STRDISGSTAPPLICABLE>\n`;
      vXml += `       <CONTENTNEGISPOS>No</CONTENTNEGISPOS>\n`;
      vXml += `       <ISLASTDEEMEDPOSITIVE>No</ISLASTDEEMEDPOSITIVE>\n`;
      vXml += `       <ISAUTONEGATE>No</ISAUTONEGATE>\n`;
      vXml += `       <ISCUSTOMSCLEARANCE>No</ISCUSTOMSCLEARANCE>\n`;
      vXml += `       <ISTRACKCOMPONENT>No</ISTRACKCOMPONENT>\n`;
      vXml += `       <ISTRACKPRODUCTION>No</ISTRACKPRODUCTION>\n`;
      vXml += `       <ISPRIMARYITEM>No</ISPRIMARYITEM>\n`;
      vXml += `       <ISSCRAP>No</ISSCRAP>\n`;
      vXml += `       <RATE>${rate.toFixed(2)}/${escapeXml(unit)}</RATE>\n`;
      vXml += `       <AMOUNT>${taxableAmt.toFixed(2)}</AMOUNT>\n`;
      vXml += `       <ACTUALQTY> ${qty.toFixed(2)} ${escapeXml(unit)}</ACTUALQTY>\n`;
      vXml += `       <BILLEDQTY> ${qty.toFixed(2)} ${escapeXml(unit)}</BILLEDQTY>\n`;
      vXml += `       <BATCHALLOCATIONS.LIST>\n`;
      vXml += `        <GODOWNNAME>${escapeXml(godown)}</GODOWNNAME>\n`;
      vXml += `        <BATCHNAME>Primary Batch</BATCHNAME>\n`;
      vXml += `        <DESTINATIONGODOWNNAME>${escapeXml(godown)}</DESTINATIONGODOWNNAME>\n`;
      vXml += `        <INDENTNO>&#4; Not Applicable</INDENTNO>\n`;
      vXml += `        <ORDERNO>&#4; Not Applicable</ORDERNO>\n`;
      vXml += `        <TRACKINGNUMBER>&#4; Not Applicable</TRACKINGNUMBER>\n`;
      vXml += `        <DYNAMICCSTISCLEARED>No</DYNAMICCSTISCLEARED>\n`;
      vXml += `        <AMOUNT>${taxableAmt.toFixed(2)}</AMOUNT>\n`;
      vXml += `        <ACTUALQTY> ${qty.toFixed(2)} ${escapeXml(unit)}</ACTUALQTY>\n`;
      vXml += `        <BILLEDQTY> ${qty.toFixed(2)} ${escapeXml(unit)}</BILLEDQTY>\n`;
      vXml += `        <ADDITIONALDETAILS.LIST>        </ADDITIONALDETAILS.LIST>\n`;
      vXml += `        <VOUCHERCOMPONENTLIST.LIST>        </VOUCHERCOMPONENTLIST.LIST>\n`;
      vXml += `       </BATCHALLOCATIONS.LIST>\n`;
      vXml += `       <ACCOUNTINGALLOCATIONS.LIST>\n`;
      vXml += `        <OLDAUDITENTRYIDS.LIST TYPE="Number">\n`;
      vXml += `         <OLDAUDITENTRYIDS>-1</OLDAUDITENTRYIDS>\n`;
      vXml += `        </OLDAUDITENTRYIDS.LIST>\n`;
      vXml += `        <LEDGERNAME>Sales</LEDGERNAME>\n`;
      vXml += `        <GSTCLASS>&#4; Not Applicable</GSTCLASS>\n`;
      vXml += `        <GSTOVRDNISTAXONMRPAPPLICABLE>&#4; Not Applicable</GSTOVRDNISTAXONMRPAPPLICABLE>\n`;
      vXml += `        <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>\n`;
      vXml += `        <LEDGERFROMITEM>No</LEDGERFROMITEM>\n`;
      vXml += `        <REMOVEZEROENTRIES>No</REMOVEZEROENTRIES>\n`;
      vXml += `        <ISRCMAPPLICABLE>No</ISRCMAPPLICABLE>\n`;
      vXml += `        <ISSYSTEM>No</ISSYSTEM>\n`;
      vXml += `        <ISPARTYLEDGER>No</ISPARTYLEDGER>\n`;
      vXml += `        <GSTOVERRIDDEN>No</GSTOVERRIDDEN>\n`;
      vXml += `        <ISGSTASSESSABLEVALUEOVERRIDDEN>No</ISGSTASSESSABLEVALUEOVERRIDDEN>\n`;
      vXml += `        <STRDISGSTAPPLICABLE>No</STRDISGSTAPPLICABLE>\n`;
      vXml += `        <STRDGSTISPARTYLEDGER>No</STRDGSTISPARTYLEDGER>\n`;
      vXml += `        <STRDGSTISDUTYLEDGER>No</STRDGSTISDUTYLEDGER>\n`;
      vXml += `        <CONTENTNEGISPOS>No</CONTENTNEGISPOS>\n`;
      vXml += `        <ISLASTDEEMEDPOSITIVE>No</ISLASTDEEMEDPOSITIVE>\n`;
      vXml += `        <ISCAPVATTAXALTERED>No</ISCAPVATTAXALTERED>\n`;
      vXml += `        <ISCAPVATNOTCLAIMED>No</ISCAPVATNOTCLAIMED>\n`;
      vXml += `        <AMOUNT>${taxableAmt.toFixed(2)}</AMOUNT>\n`;
      vXml += `        <SERVICETAXDETAILS.LIST>        </SERVICETAXDETAILS.LIST>\n`;
      vXml += `        <BANKALLOCATIONS.LIST>        </BANKALLOCATIONS.LIST>\n`;
      vXml += `        <BILLALLOCATIONS.LIST>        </BILLALLOCATIONS.LIST>\n`;
      vXml += `        <INTERESTCOLLECTION.LIST>        </INTERESTCOLLECTION.LIST>\n`;
      vXml += `        <OLDAUDITENTRIES.LIST>        </OLDAUDITENTRIES.LIST>\n`;
      vXml += `        <ACCOUNTAUDITENTRIES.LIST>        </ACCOUNTAUDITENTRIES.LIST>\n`;
      vXml += `        <AUDITENTRIES.LIST>        </AUDITENTRIES.LIST>\n`;
      vXml += `        <INPUTCRALLOCS.LIST>        </INPUTCRALLOCS.LIST>\n`;
      vXml += `        <DUTYHEADDETAILS.LIST>        </DUTYHEADDETAILS.LIST>\n`;
      vXml += `        <EXCISEDUTYHEADDETAILS.LIST>        </EXCISEDUTYHEADDETAILS.LIST>\n`;
      vXml += `        <RATEDETAILS.LIST>        </RATEDETAILS.LIST>\n`;
      vXml += `        <SUMMARYALLOCS.LIST>        </SUMMARYALLOCS.LIST>\n`;
      vXml += `        <CENVATDUTYALLOCATIONS.LIST>        </CENVATDUTYALLOCATIONS.LIST>\n`;
      vXml += `        <STPYMTDETAILS.LIST>        </STPYMTDETAILS.LIST>\n`;
      vXml += `        <EXCISEPAYMENTALLOCATIONS.LIST>        </EXCISEPAYMENTALLOCATIONS.LIST>\n`;
      vXml += `        <TAXBILLALLOCATIONS.LIST>        </TAXBILLALLOCATIONS.LIST>\n`;
      vXml += `        <TAXOBJECTALLOCATIONS.LIST>        </TAXOBJECTALLOCATIONS.LIST>\n`;
      vXml += `        <TDSEXPENSEALLOCATIONS.LIST>        </TDSEXPENSEALLOCATIONS.LIST>\n`;
      vXml += `        <VATSTATUTORYDETAILS.LIST>        </VATSTATUTORYDETAILS.LIST>\n`;
      vXml += `        <COSTTRACKALLOCATIONS.LIST>        </COSTTRACKALLOCATIONS.LIST>\n`;
      vXml += `        <REFVOUCHERDETAILS.LIST>        </REFVOUCHERDETAILS.LIST>\n`;
      vXml += `        <INVOICEWISEDETAILS.LIST>        </INVOICEWISEDETAILS.LIST>\n`;
      vXml += `        <VATITCDETAILS.LIST>        </VATITCDETAILS.LIST>\n`;
      vXml += `        <ADVANCETAXDETAILS.LIST>        </ADVANCETAXDETAILS.LIST>\n`;
      vXml += `        <TAXTYPEALLOCATIONS.LIST>        </TAXTYPEALLOCATIONS.LIST>\n`;
      vXml += `       </ACCOUNTINGALLOCATIONS.LIST>\n`;
      vXml += `       <DUTYHEADDETAILS.LIST>       </DUTYHEADDETAILS.LIST>\n`;
      vXml += `       <RATEDETAILS.LIST>\n`;
      vXml += `        <GSTRATEDUTYHEAD>CGST</GSTRATEDUTYHEAD>\n`;
      vXml += `        <GSTRATEVALUATIONTYPE>Based on Value</GSTRATEVALUATIONTYPE>\n`;
      vXml += `        <GSTRATE> ${itemCgstRate.toFixed(2)}</GSTRATE>\n`;
      vXml += `       </RATEDETAILS.LIST>\n`;
      vXml += `       <RATEDETAILS.LIST>\n`;
      vXml += `        <GSTRATEDUTYHEAD>SGST/UTGST</GSTRATEDUTYHEAD>\n`;
      vXml += `        <GSTRATEVALUATIONTYPE>Based on Value</GSTRATEVALUATIONTYPE>\n`;
      vXml += `        <GSTRATE> ${itemSgstRate.toFixed(2)}</GSTRATE>\n`;
      vXml += `       </RATEDETAILS.LIST>\n`;
      vXml += `       <RATEDETAILS.LIST>\n`;
      vXml += `        <GSTRATEDUTYHEAD>IGST</GSTRATEDUTYHEAD>\n`;
      vXml += `        <GSTRATEVALUATIONTYPE>Based on Value</GSTRATEVALUATIONTYPE>\n`;
      vXml += `        <GSTRATE> ${itemIgstRate.toFixed(2)}</GSTRATE>\n`;
      vXml += `       </RATEDETAILS.LIST>\n`;
      vXml += `       <RATEDETAILS.LIST>\n`;
      vXml += `        <GSTRATEDUTYHEAD>Cess</GSTRATEDUTYHEAD>\n`;
      vXml += `        <GSTRATEVALUATIONTYPE>&#4; Not Applicable</GSTRATEVALUATIONTYPE>\n`;
      vXml += `       </RATEDETAILS.LIST>\n`;
      vXml += `       <RATEDETAILS.LIST>\n`;
      vXml += `        <GSTRATEDUTYHEAD>State Cess</GSTRATEDUTYHEAD>\n`;
      vXml += `        <GSTRATEVALUATIONTYPE>Based on Value</GSTRATEVALUATIONTYPE>\n`;
      vXml += `       </RATEDETAILS.LIST>\n`;
      vXml += `       <SUPPLEMENTARYDUTYHEADDETAILS.LIST>       </SUPPLEMENTARYDUTYHEADDETAILS.LIST>\n`;
      vXml += `       <TAXOBJECTALLOCATIONS.LIST>       </TAXOBJECTALLOCATIONS.LIST>\n`;
      vXml += `       <REFVOUCHERDETAILS.LIST>       </REFVOUCHERDETAILS.LIST>\n`;
      vXml += `       <EXCISEALLOCATIONS.LIST>       </EXCISEALLOCATIONS.LIST>\n`;
      vXml += `       <EXPENSEALLOCATIONS.LIST>       </EXPENSEALLOCATIONS.LIST>\n`;
      vXml += `      </ALLINVENTORYENTRIES.LIST>\n`;
    });
  }

  // Mandatory empty list placeholders for Tally schema compliance
  vXml += `      <CONTRITRANS.LIST>      </CONTRITRANS.LIST>\n`;
  vXml += `      <EWAYBILLERRORLIST.LIST>      </EWAYBILLERRORLIST.LIST>\n`;
  vXml += `      <IRNERRORLIST.LIST>      </IRNERRORLIST.LIST>\n`;
  vXml += `      <HARYANAVAT.LIST>      </HARYANAVAT.LIST>\n`;
  vXml += `      <SUPPLEMENTARYDUTYHEADDETAILS.LIST>      </SUPPLEMENTARYDUTYHEADDETAILS.LIST>\n`;
  vXml += `      <INVOICEDELNOTES.LIST>      </INVOICEDELNOTES.LIST>\n`;
  vXml += `      <INVOICEORDERLIST.LIST>      </INVOICEORDERLIST.LIST>\n`;
  vXml += `      <INVOICEINDENTLIST.LIST>      </INVOICEINDENTLIST.LIST>\n`;
  vXml += `      <ATTENDANCEENTRIES.LIST>      </ATTENDANCEENTRIES.LIST>\n`;
  vXml += `      <ORIGINVOICEDETAILS.LIST>      </ORIGINVOICEDETAILS.LIST>\n`;
  vXml += `      <INVOICEEXPORTLIST.LIST>      </INVOICEEXPORTLIST.LIST>\n`;

  // Ledger Entries
  // Party Ledger Entry (Debited - Negative Total Amount in Tally XML)
  vXml += `      <LEDGERENTRIES.LIST>\n`;
  vXml += `       <OLDAUDITENTRYIDS.LIST TYPE="Number">\n`;
  vXml += `        <OLDAUDITENTRYIDS>-1</OLDAUDITENTRYIDS>\n`;
  vXml += `       </OLDAUDITENTRYIDS.LIST>\n`;
  vXml += `       <LEDGERNAME>${escapeXml(partyName)}</LEDGERNAME>\n`;
  vXml += `       <GSTCLASS>&#4; Not Applicable</GSTCLASS>\n`;
  vXml += `       <GSTOVRDNISTAXONMRPAPPLICABLE>&#4; Not Applicable</GSTOVRDNISTAXONMRPAPPLICABLE>\n`;
  vXml += `       <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>\n`;
  vXml += `       <LEDGERFROMITEM>No</LEDGERFROMITEM>\n`;
  vXml += `       <REMOVEZEROENTRIES>No</REMOVEZEROENTRIES>\n`;
  vXml += `       <ISRCMAPPLICABLE>No</ISRCMAPPLICABLE>\n`;
  vXml += `       <ISSYSTEM>No</ISSYSTEM>\n`;
  vXml += `       <ISPARTYLEDGER>Yes</ISPARTYLEDGER>\n`;
  vXml += `       <GSTOVERRIDDEN>No</GSTOVERRIDDEN>\n`;
  vXml += `       <ISGSTASSESSABLEVALUEOVERRIDDEN>No</ISGSTASSESSABLEVALUEOVERRIDDEN>\n`;
  vXml += `       <STRDISGSTAPPLICABLE>No</STRDISGSTAPPLICABLE>\n`;
  vXml += `       <STRDGSTISPARTYLEDGER>No</STRDGSTISPARTYLEDGER>\n`;
  vXml += `       <STRDGSTISDUTYLEDGER>No</STRDGSTISDUTYLEDGER>\n`;
  vXml += `       <CONTENTNEGISPOS>No</CONTENTNEGISPOS>\n`;
  vXml += `       <ISLASTDEEMEDPOSITIVE>Yes</ISLASTDEEMEDPOSITIVE>\n`;
  vXml += `       <ISCAPVATTAXALTERED>No</ISCAPVATTAXALTERED>\n`;
  vXml += `       <ISCAPVATNOTCLAIMED>No</ISCAPVATNOTCLAIMED>\n`;
  vXml += `       <AMOUNT>-${totalAmount.toFixed(2)}</AMOUNT>\n`;
  vXml += `       <SERVICETAXDETAILS.LIST>       </SERVICETAXDETAILS.LIST>\n`;
  vXml += `       <BANKALLOCATIONS.LIST>       </BANKALLOCATIONS.LIST>\n`;
  vXml += `       <BILLALLOCATIONS.LIST>\n`;
  vXml += `        <NAME>${escapeXml(billNo)}</NAME>\n`;
  vXml += `        <BILLTYPE>New Ref</BILLTYPE>\n`;
  vXml += `        <TDSDEDUCTEEISSPECIALRATE>No</TDSDEDUCTEEISSPECIALRATE>\n`;
  vXml += `        <AMOUNT>-${totalAmount.toFixed(2)}</AMOUNT>\n`;
  vXml += `        <INTERESTCOLLECTION.LIST>        </INTERESTCOLLECTION.LIST>\n`;
  vXml += `        <STBILLCATEGORIES.LIST>        </STBILLCATEGORIES.LIST>\n`;
  vXml += `       </BILLALLOCATIONS.LIST>\n`;
  vXml += `       <INTERESTCOLLECTION.LIST>       </INTERESTCOLLECTION.LIST>\n`;
  vXml += `       <OLDAUDITENTRIES.LIST>       </OLDAUDITENTRIES.LIST>\n`;
  vXml += `       <ACCOUNTAUDITENTRIES.LIST>       </ACCOUNTAUDITENTRIES.LIST>\n`;
  vXml += `       <AUDITENTRIES.LIST>       </AUDITENTRIES.LIST>\n`;
  vXml += `       <INPUTCRALLOCS.LIST>       </INPUTCRALLOCS.LIST>\n`;
  vXml += `       <DUTYHEADDETAILS.LIST>       </DUTYHEADDETAILS.LIST>\n`;
  vXml += `       <EXCISEDUTYHEADDETAILS.LIST>       </EXCISEDUTYHEADDETAILS.LIST>\n`;
  vXml += `       <RATEDETAILS.LIST>       </RATEDETAILS.LIST>\n`;
  vXml += `       <SUMMARYALLOCS.LIST>       </SUMMARYALLOCS.LIST>\n`;
  vXml += `       <CENVATDUTYALLOCATIONS.LIST>       </CENVATDUTYALLOCATIONS.LIST>\n`;
  vXml += `       <STPYMTDETAILS.LIST>       </STPYMTDETAILS.LIST>\n`;
  vXml += `       <EXCISEPAYMENTALLOCATIONS.LIST>       </EXCISEPAYMENTALLOCATIONS.LIST>\n`;
  vXml += `       <TAXBILLALLOCATIONS.LIST>       </TAXBILLALLOCATIONS.LIST>\n`;
  vXml += `       <TAXOBJECTALLOCATIONS.LIST>       </TAXOBJECTALLOCATIONS.LIST>\n`;
  vXml += `       <TDSEXPENSEALLOCATIONS.LIST>       </TDSEXPENSEALLOCATIONS.LIST>\n`;
  vXml += `       <VATSTATUTORYDETAILS.LIST>       </VATSTATUTORYDETAILS.LIST>\n`;
  vXml += `       <COSTTRACKALLOCATIONS.LIST>       </COSTTRACKALLOCATIONS.LIST>\n`;
  vXml += `       <REFVOUCHERDETAILS.LIST>       </REFVOUCHERDETAILS.LIST>\n`;
  vXml += `       <INVOICEWISEDETAILS.LIST>       </INVOICEWISEDETAILS.LIST>\n`;
  vXml += `       <VATITCDETAILS.LIST>       </VATITCDETAILS.LIST>\n`;
  vXml += `       <ADVANCETAXDETAILS.LIST>       </ADVANCETAXDETAILS.LIST>\n`;
  vXml += `       <TAXTYPEALLOCATIONS.LIST>       </TAXTYPEALLOCATIONS.LIST>\n`;
  vXml += `      </LEDGERENTRIES.LIST>\n`;

  // SGST Ledger Entry
  if (sgstAmount > 0) {
    vXml += `      <LEDGERENTRIES.LIST>\n`;
    vXml += `       <OLDAUDITENTRYIDS.LIST TYPE="Number">\n`;
    vXml += `        <OLDAUDITENTRYIDS>-1</OLDAUDITENTRYIDS>\n`;
    vXml += `       </OLDAUDITENTRYIDS.LIST>\n`;
    vXml += `       <RATEOFINVOICETAX.LIST TYPE="Number">\n`;
    vXml += `        <RATEOFINVOICETAX> 2.50</RATEOFINVOICETAX>\n`;
    vXml += `       </RATEOFINVOICETAX.LIST>\n`;
    vXml += `       <ROUNDTYPE>&#4; Not Applicable</ROUNDTYPE>\n`;
    vXml += `       <LEDGERNAME>SGST</LEDGERNAME>\n`;
    vXml += `       <GSTCLASS>&#4; Not Applicable</GSTCLASS>\n`;
    vXml += `       <GSTOVRDNISTAXONMRPAPPLICABLE>&#4; Not Applicable</GSTOVRDNISTAXONMRPAPPLICABLE>\n`;
    vXml += `       <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>\n`;
    vXml += `       <LEDGERFROMITEM>No</LEDGERFROMITEM>\n`;
    vXml += `       <REMOVEZEROENTRIES>No</REMOVEZEROENTRIES>\n`;
    vXml += `       <ISRCMAPPLICABLE>No</ISRCMAPPLICABLE>\n`;
    vXml += `       <ISSYSTEM>No</ISSYSTEM>\n`;
    vXml += `       <ISPARTYLEDGER>No</ISPARTYLEDGER>\n`;
    vXml += `       <GSTOVERRIDDEN>No</GSTOVERRIDDEN>\n`;
    vXml += `       <ISGSTASSESSABLEVALUEOVERRIDDEN>No</ISGSTASSESSABLEVALUEOVERRIDDEN>\n`;
    vXml += `       <STRDISGSTAPPLICABLE>No</STRDISGSTAPPLICABLE>\n`;
    vXml += `       <STRDGSTISPARTYLEDGER>No</STRDGSTISPARTYLEDGER>\n`;
    vXml += `       <STRDGSTISDUTYLEDGER>No</STRDGSTISDUTYLEDGER>\n`;
    vXml += `       <CONTENTNEGISPOS>No</CONTENTNEGISPOS>\n`;
    vXml += `       <ISLASTDEEMEDPOSITIVE>No</ISLASTDEEMEDPOSITIVE>\n`;
    vXml += `       <ISCAPVATTAXALTERED>No</ISCAPVATTAXALTERED>\n`;
    vXml += `       <ISCAPVATNOTCLAIMED>No</ISCAPVATNOTCLAIMED>\n`;
    vXml += `       <AMOUNT>${sgstAmount.toFixed(2)}</AMOUNT>\n`;
    vXml += `       <VATEXPAMOUNT>${sgstAmount.toFixed(2)}</VATEXPAMOUNT>\n`;
    vXml += `       <SERVICETAXDETAILS.LIST>       </SERVICETAXDETAILS.LIST>\n`;
    vXml += `       <BANKALLOCATIONS.LIST>       </BANKALLOCATIONS.LIST>\n`;
    vXml += `       <BILLALLOCATIONS.LIST>       </BILLALLOCATIONS.LIST>\n`;
    vXml += `       <INTERESTCOLLECTION.LIST>       </INTERESTCOLLECTION.LIST>\n`;
    vXml += `       <OLDAUDITENTRIES.LIST>       </OLDAUDITENTRIES.LIST>\n`;
    vXml += `       <ACCOUNTAUDITENTRIES.LIST>       </ACCOUNTAUDITENTRIES.LIST>\n`;
    vXml += `       <AUDITENTRIES.LIST>       </AUDITENTRIES.LIST>\n`;
    vXml += `       <INPUTCRALLOCS.LIST>       </INPUTCRALLOCS.LIST>\n`;
    vXml += `       <DUTYHEADDETAILS.LIST>       </DUTYHEADDETAILS.LIST>\n`;
    vXml += `       <EXCISEDUTYHEADDETAILS.LIST>       </EXCISEDUTYHEADDETAILS.LIST>\n`;
    vXml += `       <RATEDETAILS.LIST>       </RATEDETAILS.LIST>\n`;
    vXml += `       <SUMMARYALLOCS.LIST>       </SUMMARYALLOCS.LIST>\n`;
    vXml += `       <CENVATDUTYALLOCATIONS.LIST>       </CENVATDUTYALLOCATIONS.LIST>\n`;
    vXml += `       <STPYMTDETAILS.LIST>       </STPYMTDETAILS.LIST>\n`;
    vXml += `       <EXCISEPAYMENTALLOCATIONS.LIST>       </EXCISEPAYMENTALLOCATIONS.LIST>\n`;
    vXml += `       <TAXBILLALLOCATIONS.LIST>       </TAXBILLALLOCATIONS.LIST>\n`;
    vXml += `       <TAXOBJECTALLOCATIONS.LIST>       </TAXOBJECTALLOCATIONS.LIST>\n`;
    vXml += `       <TDSEXPENSEALLOCATIONS.LIST>       </TDSEXPENSEALLOCATIONS.LIST>\n`;
    vXml += `       <VATSTATUTORYDETAILS.LIST>       </VATSTATUTORYDETAILS.LIST>\n`;
    vXml += `       <COSTTRACKALLOCATIONS.LIST>       </COSTTRACKALLOCATIONS.LIST>\n`;
    vXml += `       <REFVOUCHERDETAILS.LIST>       </REFVOUCHERDETAILS.LIST>\n`;
    vXml += `       <INVOICEWISEDETAILS.LIST>       </INVOICEWISEDETAILS.LIST>\n`;
    vXml += `       <VATITCDETAILS.LIST>       </VATITCDETAILS.LIST>\n`;
    vXml += `       <ADVANCETAXDETAILS.LIST>       </ADVANCETAXDETAILS.LIST>\n`;
    vXml += `       <TAXTYPEALLOCATIONS.LIST>       </TAXTYPEALLOCATIONS.LIST>\n`;
    vXml += `      </LEDGERENTRIES.LIST>\n`;
  }

  // CGST Ledger Entry
  if (cgstAmount > 0) {
    vXml += `      <LEDGERENTRIES.LIST>\n`;
    vXml += `       <OLDAUDITENTRYIDS.LIST TYPE="Number">\n`;
    vXml += `        <OLDAUDITENTRYIDS>-1</OLDAUDITENTRYIDS>\n`;
    vXml += `       </OLDAUDITENTRYIDS.LIST>\n`;
    vXml += `       <RATEOFINVOICETAX.LIST TYPE="Number">\n`;
    vXml += `        <RATEOFINVOICETAX> 2.50</RATEOFINVOICETAX>\n`;
    vXml += `       </RATEOFINVOICETAX.LIST>\n`;
    vXml += `       <ROUNDTYPE>&#4; Not Applicable</ROUNDTYPE>\n`;
    vXml += `       <LEDGERNAME>CGST</LEDGERNAME>\n`;
    vXml += `       <GSTCLASS>&#4; Not Applicable</GSTCLASS>\n`;
    vXml += `       <GSTOVRDNISTAXONMRPAPPLICABLE>&#4; Not Applicable</GSTOVRDNISTAXONMRPAPPLICABLE>\n`;
    vXml += `       <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>\n`;
    vXml += `       <LEDGERFROMITEM>No</LEDGERFROMITEM>\n`;
    vXml += `       <REMOVEZEROENTRIES>No</REMOVEZEROENTRIES>\n`;
    vXml += `       <ISRCMAPPLICABLE>No</ISRCMAPPLICABLE>\n`;
    vXml += `       <ISSYSTEM>No</ISSYSTEM>\n`;
    vXml += `       <ISPARTYLEDGER>No</ISPARTYLEDGER>\n`;
    vXml += `       <GSTOVERRIDDEN>No</GSTOVERRIDDEN>\n`;
    vXml += `       <ISGSTASSESSABLEVALUEOVERRIDDEN>No</ISGSTASSESSABLEVALUEOVERRIDDEN>\n`;
    vXml += `       <STRDISGSTAPPLICABLE>No</STRDISGSTAPPLICABLE>\n`;
    vXml += `       <STRDGSTISPARTYLEDGER>No</STRDGSTISPARTYLEDGER>\n`;
    vXml += `       <STRDGSTISDUTYLEDGER>No</STRDGSTISDUTYLEDGER>\n`;
    vXml += `       <CONTENTNEGISPOS>No</CONTENTNEGISPOS>\n`;
    vXml += `       <ISLASTDEEMEDPOSITIVE>No</ISLASTDEEMEDPOSITIVE>\n`;
    vXml += `       <ISCAPVATTAXALTERED>No</ISCAPVATTAXALTERED>\n`;
    vXml += `       <ISCAPVATNOTCLAIMED>No</ISCAPVATNOTCLAIMED>\n`;
    vXml += `       <AMOUNT>${cgstAmount.toFixed(2)}</AMOUNT>\n`;
    vXml += `       <VATEXPAMOUNT>${cgstAmount.toFixed(2)}</VATEXPAMOUNT>\n`;
    vXml += `       <SERVICETAXDETAILS.LIST>       </SERVICETAXDETAILS.LIST>\n`;
    vXml += `       <BANKALLOCATIONS.LIST>       </BANKALLOCATIONS.LIST>\n`;
    vXml += `       <BILLALLOCATIONS.LIST>       </BILLALLOCATIONS.LIST>\n`;
    vXml += `       <INTERESTCOLLECTION.LIST>       </INTERESTCOLLECTION.LIST>\n`;
    vXml += `       <OLDAUDITENTRIES.LIST>       </OLDAUDITENTRIES.LIST>\n`;
    vXml += `       <ACCOUNTAUDITENTRIES.LIST>       </ACCOUNTAUDITENTRIES.LIST>\n`;
    vXml += `       <AUDITENTRIES.LIST>       </AUDITENTRIES.LIST>\n`;
    vXml += `       <INPUTCRALLOCS.LIST>       </INPUTCRALLOCS.LIST>\n`;
    vXml += `       <DUTYHEADDETAILS.LIST>       </DUTYHEADDETAILS.LIST>\n`;
    vXml += `       <EXCISEDUTYHEADDETAILS.LIST>       </EXCISEDUTYHEADDETAILS.LIST>\n`;
    vXml += `       <RATEDETAILS.LIST>       </RATEDETAILS.LIST>\n`;
    vXml += `       <SUMMARYALLOCS.LIST>       </SUMMARYALLOCS.LIST>\n`;
    vXml += `       <CENVATDUTYALLOCATIONS.LIST>       </CENVATDUTYALLOCATIONS.LIST>\n`;
    vXml += `       <STPYMTDETAILS.LIST>       </STPYMTDETAILS.LIST>\n`;
    vXml += `       <EXCISEPAYMENTALLOCATIONS.LIST>       </EXCISEPAYMENTALLOCATIONS.LIST>\n`;
    vXml += `       <TAXBILLALLOCATIONS.LIST>       </TAXBILLALLOCATIONS.LIST>\n`;
    vXml += `       <TAXOBJECTALLOCATIONS.LIST>       </TAXOBJECTALLOCATIONS.LIST>\n`;
    vXml += `       <TDSEXPENSEALLOCATIONS.LIST>       </TDSEXPENSEALLOCATIONS.LIST>\n`;
    vXml += `       <VATSTATUTORYDETAILS.LIST>       </VATSTATUTORYDETAILS.LIST>\n`;
    vXml += `       <COSTTRACKALLOCATIONS.LIST>       </COSTTRACKALLOCATIONS.LIST>\n`;
    vXml += `       <REFVOUCHERDETAILS.LIST>       </REFVOUCHERDETAILS.LIST>\n`;
    vXml += `       <INVOICEWISEDETAILS.LIST>       </INVOICEWISEDETAILS.LIST>\n`;
    vXml += `       <VATITCDETAILS.LIST>       </VATITCDETAILS.LIST>\n`;
    vXml += `       <ADVANCETAXDETAILS.LIST>       </ADVANCETAXDETAILS.LIST>\n`;
    vXml += `       <TAXTYPEALLOCATIONS.LIST>       </TAXTYPEALLOCATIONS.LIST>\n`;
    vXml += `      </LEDGERENTRIES.LIST>\n`;
  }

  // IGST Ledger Entry
  if (igstAmount > 0) {
    vXml += `      <LEDGERENTRIES.LIST>\n`;
    vXml += `       <OLDAUDITENTRYIDS.LIST TYPE="Number">\n`;
    vXml += `        <OLDAUDITENTRYIDS>-1</OLDAUDITENTRYIDS>\n`;
    vXml += `       </OLDAUDITENTRYIDS.LIST>\n`;
    vXml += `       <RATEOFINVOICETAX.LIST TYPE="Number">\n`;
    vXml += `        <RATEOFINVOICETAX> 5.00</RATEOFINVOICETAX>\n`;
    vXml += `       </RATEOFINVOICETAX.LIST>\n`;
    vXml += `       <ROUNDTYPE>&#4; Not Applicable</ROUNDTYPE>\n`;
    vXml += `       <LEDGERNAME>IGST</LEDGERNAME>\n`;
    vXml += `       <GSTCLASS>&#4; Not Applicable</GSTCLASS>\n`;
    vXml += `       <GSTOVRDNISTAXONMRPAPPLICABLE>&#4; Not Applicable</GSTOVRDNISTAXONMRPAPPLICABLE>\n`;
    vXml += `       <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>\n`;
    vXml += `       <LEDGERFROMITEM>No</LEDGERFROMITEM>\n`;
    vXml += `       <REMOVEZEROENTRIES>No</REMOVEZEROENTRIES>\n`;
    vXml += `       <ISRCMAPPLICABLE>No</ISRCMAPPLICABLE>\n`;
    vXml += `       <ISSYSTEM>No</ISSYSTEM>\n`;
    vXml += `       <ISPARTYLEDGER>No</ISPARTYLEDGER>\n`;
    vXml += `       <GSTOVERRIDDEN>No</GSTOVERRIDDEN>\n`;
    vXml += `       <ISGSTASSESSABLEVALUEOVERRIDDEN>No</ISGSTASSESSABLEVALUEOVERRIDDEN>\n`;
    vXml += `       <STRDISGSTAPPLICABLE>No</STRDISGSTAPPLICABLE>\n`;
    vXml += `       <STRDGSTISPARTYLEDGER>No</STRDGSTISPARTYLEDGER>\n`;
    vXml += `       <STRDGSTISDUTYLEDGER>No</STRDGSTISDUTYLEDGER>\n`;
    vXml += `       <CONTENTNEGISPOS>No</CONTENTNEGISPOS>\n`;
    vXml += `       <ISLASTDEEMEDPOSITIVE>No</ISLASTDEEMEDPOSITIVE>\n`;
    vXml += `       <ISCAPVATTAXALTERED>No</ISCAPVATTAXALTERED>\n`;
    vXml += `       <ISCAPVATNOTCLAIMED>No</ISCAPVATNOTCLAIMED>\n`;
    vXml += `       <AMOUNT>${igstAmount.toFixed(2)}</AMOUNT>\n`;
    vXml += `       <VATEXPAMOUNT>${igstAmount.toFixed(2)}</VATEXPAMOUNT>\n`;
    vXml += `      </LEDGERENTRIES.LIST>\n`;
  }

  // Summary fallback if no items
  if (items.length === 0) {
    const taxableSales = totalAmount - totalTaxAmount - cessAmount + discountAmount;
    vXml += `      <LEDGERENTRIES.LIST>\n`;
    vXml += `       <OLDAUDITENTRYIDS.LIST TYPE="Number">\n`;
    vXml += `        <OLDAUDITENTRYIDS>-1</OLDAUDITENTRYIDS>\n`;
    vXml += `       </OLDAUDITENTRYIDS.LIST>\n`;
    vXml += `       <LEDGERNAME>Sales</LEDGERNAME>\n`;
    vXml += `       <GSTCLASS>&#4; Not Applicable</GSTCLASS>\n`;
    vXml += `       <GSTOVRDNISTAXONMRPAPPLICABLE>&#4; Not Applicable</GSTOVRDNISTAXONMRPAPPLICABLE>\n`;
    vXml += `       <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>\n`;
    vXml += `       <LEDGERFROMITEM>No</LEDGERFROMITEM>\n`;
    vXml += `       <REMOVEZEROENTRIES>No</REMOVEZEROENTRIES>\n`;
    vXml += `       <ISRCMAPPLICABLE>No</ISRCMAPPLICABLE>\n`;
    vXml += `       <ISSYSTEM>No</ISSYSTEM>\n`;
    vXml += `       <ISPARTYLEDGER>No</ISPARTYLEDGER>\n`;
    vXml += `       <GSTOVERRIDDEN>No</GSTOVERRIDDEN>\n`;
    vXml += `       <ISGSTASSESSABLEVALUEOVERRIDDEN>No</ISGSTASSESSABLEVALUEOVERRIDDEN>\n`;
    vXml += `       <STRDISGSTAPPLICABLE>No</STRDISGSTAPPLICABLE>\n`;
    vXml += `       <STRDGSTISPARTYLEDGER>No</STRDGSTISPARTYLEDGER>\n`;
    vXml += `       <STRDGSTISDUTYLEDGER>No</STRDGSTISDUTYLEDGER>\n`;
    vXml += `       <CONTENTNEGISPOS>No</CONTENTNEGISPOS>\n`;
    vXml += `       <ISLASTDEEMEDPOSITIVE>No</ISLASTDEEMEDPOSITIVE>\n`;
    vXml += `       <ISCAPVATTAXALTERED>No</ISCAPVATTAXALTERED>\n`;
    vXml += `       <ISCAPVATNOTCLAIMED>No</ISCAPVATNOTCLAIMED>\n`;
    vXml += `       <AMOUNT>${taxableSales.toFixed(2)}</AMOUNT>\n`;
    vXml += `      </LEDGERENTRIES.LIST>\n`;
  }

  vXml += `      <GST.LIST>\n`;
  vXml += `       <PURPOSETYPE>GST</PURPOSETYPE>\n`;
  vXml += `       <STAT.LIST>\n`;
  vXml += `        <PURPOSETYPE>GST</PURPOSETYPE>\n`;
  vXml += `        <STATKEY>&#4; Auto Stat Number</STATKEY>\n`;
  vXml += `        <ISFETCHEDONLY>No</ISFETCHEDONLY>\n`;
  vXml += `        <ISDELETED>No</ISDELETED>\n`;
  vXml += `        <TALLYCONTENTUSER.LIST>        </TALLYCONTENTUSER.LIST>\n`;
  vXml += `       </STAT.LIST>\n`;
  vXml += `      </GST.LIST>\n`;
  vXml += `      <UAEVAT.LIST>      </UAEVAT.LIST>\n`;
  vXml += `      <STKJRNLADDLCOSTDETAILS.LIST>      </STKJRNLADDLCOSTDETAILS.LIST>\n`;
  vXml += `      <PAYROLLMODEOFPAYMENT.LIST>      </PAYROLLMODEOFPAYMENT.LIST>\n`;
  vXml += `      <ATTDRECORDS.LIST>      </ATTDRECORDS.LIST>\n`;
  vXml += `      <GSTEWAYCONSIGNORADDRESS.LIST>      </GSTEWAYCONSIGNORADDRESS.LIST>\n`;
  vXml += `      <GSTEWAYCONSIGNEEADDRESS.LIST>      </GSTEWAYCONSIGNEEADDRESS.LIST>\n`;
  vXml += `      <TEMPGSTRATEDETAILS.LIST>      </TEMPGSTRATEDETAILS.LIST>\n`;
  vXml += `      <TEMPGSTADVADJUSTED.LIST>      </TEMPGSTADVADJUSTED.LIST>\n`;
  vXml += `      <GSTBUYERADDRESS.LIST>      </GSTBUYERADDRESS.LIST>\n`;
  vXml += `      <GSTCONSIGNEEADDRESS.LIST>      </GSTCONSIGNEEADDRESS.LIST>\n`;
  vXml += `     </VOUCHER>\n`;
  vXml += `    </TALLYMESSAGE>\n`;

  return vXml;
}

const TallyXmlHelper = {
  generateTallySalesXml,
  formatDateToTally,
  escapeXml,
};

export default TallyXmlHelper;
